// src/App.tsx
import { calculateStats } from './core/stats';
import { NATURE_MAP } from './data/mock-natures';
import { useEffect, useState } from 'react';
import { Header } from './components/layout/Header';
import type { TabKey } from './components/layout/TabBar';
import { TeamPanel } from './features/team/TeamPanel';
import { RosterPanel } from './features/team/RosterPanel';
import { BattlePanel } from './features/battle/BattlePanel';
import { ComparisonView } from './features/battle/ComparisonView';

import { SpiritPicker } from './features/team/SpiritPicker';
import { SkillPicker } from './features/team/SkillPicker';
import { StatEditor } from './features/team/StatEditor';
import { NatureLevelEditor } from './features/team/NatureLevelEditor';

import { useTeamStore } from './store/useTeamStore';
import { useScenarioStore } from './store/useScenarioStore';
import { useUiStore } from './store/useUiStore';
import { SPIRIT_MAP } from './data/mock-spirits';
import { SKILL_MAP } from './data/mock-skills';
import { makeDefaultEvs, makeDefaultIvs } from './core/stats';
import type { StatKey } from './core/types';

function App() {
  const [tab, setTab] = useState<TabKey>('config');

  // team
  const team = useTeamStore((s) => s.team);
  const setSlotSpirit = useTeamStore((s) => s.setSlotSpirit);
  const setSlotNature = useTeamStore((s) => s.setSlotNature);
  
  const toggleSlotSkill = useTeamStore((s) => s.toggleSlotSkill);
  const setSlotIv = useTeamStore((s) => s.setSlotIv);
  const setSlotEv = useTeamStore((s) => s.setSlotEv);

  // scenario
  const selectedSkillId = useScenarioStore((s) => s.selectedSkillId);
  const calculate = useScenarioStore((s) => s.calculate);

  // ui
  const spiritPickerSlot = useUiStore((s) => s.spiritPickerSlot);
  const skillPickerSlot = useUiStore((s) => s.skillPickerSlot);
  const statEditorSlot = useUiStore((s) => s.statEditorSlot);
  const natureEditorSlot = useUiStore((s) => s.natureEditorSlot);
  const closeSpiritPicker = useUiStore((s) => s.closeSpiritPicker);
  const closeSkillPicker = useUiStore((s) => s.closeSkillPicker);
  const closeStatEditor = useUiStore((s) => s.closeStatEditor);
  const closeNatureEditor = useUiStore((s) => s.closeNatureEditor);

  //
  const setSlotBaseStat = useTeamStore((s) => s.setSlotBaseStat);
const resetSlotBaseStats = useTeamStore((s) => s.resetSlotBaseStats);
  // 首次挂载：给前两个槽位放默认精灵
  useEffect(() => {
    if (!team.slots[0]?.spiritId) {
      setSlotSpirit(0, 'flamebeast');
      setSlotNature(0, 'adamant');
    }
    if (!team.slots[1]?.spiritId) {
      setSlotSpirit(1, 'aquaking');
      setSlotNature(1, 'bold');
    }
  }, [team.slots, setSlotSpirit, setSlotNature]);

 

  const skillPickerSlotData =
    skillPickerSlot !== null ? team.slots[skillPickerSlot] : null;
  const skillPickerSpirit = skillPickerSlotData?.spiritId
    ? SPIRIT_MAP[skillPickerSlotData.spiritId]
    : null;

  const statEditorSlotData =
    statEditorSlot !== null ? team.slots[statEditorSlot] : null;
  const statEditorSpirit = statEditorSlotData?.spiritId
    ? SPIRIT_MAP[statEditorSlotData.spiritId]
    : null;

  const natureEditorSlotData =
    natureEditorSlot !== null ? team.slots[natureEditorSlot] : null;
  const natureEditorSpirit = natureEditorSlotData?.spiritId
    ? SPIRIT_MAP[natureEditorSlotData.spiritId]
    : null;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header active={tab} onChange={setTab} />

      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'config' && (
          <div className="space-y-6">
            <TeamPanel />
            <div className="max-w-2xl mx-auto w-full">
              <ComparisonView />
            </div>
          </div>
        )}
        {tab === 'calc' && <BattlePanel />}
        {tab === 'roster' && <RosterPanel />}
      </main>

      {/* 全局弹窗 */}
      <SpiritPicker
        open={spiritPickerSlot !== null}
        onClose={closeSpiritPicker}
        currentId={
          spiritPickerSlot !== null
            ? team.slots[spiritPickerSlot]?.spiritId
            : null
        }
        onSelect={(id) => {
          if (spiritPickerSlot !== null) setSlotSpirit(spiritPickerSlot, id);
        }}
      />

      <SkillPicker
        open={skillPickerSlot !== null}
        onClose={closeSkillPicker}
        spiritName={skillPickerSpirit?.name}
        availableSkillIds={skillPickerSpirit?.skills ?? []}
        selectedSkillIds={skillPickerSlotData?.skills ?? []}
        onToggle={(skillId) => {
          if (skillPickerSlot !== null) {
            toggleSlotSkill(skillPickerSlot, skillId);
          }
        }}
      />

      <StatEditor
  open={statEditorSlot !== null}
  onClose={closeStatEditor}
  spiritName={statEditorSpirit?.name}
  baseStats={
    statEditorSlotData?.customBaseStats ??
    statEditorSpirit?.baseStats ?? {
      hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
    }
  }
  evs={statEditorSlotData?.ivs ?? makeDefaultEvs()}
  // 👇 新增：实时计算最终 PvP 面板
        finalStats={
          statEditorSlotData?.spiritId
            ? calculateStats(
                statEditorSlotData.customBaseStats ??
                  SPIRIT_MAP[statEditorSlotData.spiritId]?.baseStats ?? {
                    hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0,
                  },
                statEditorSlotData.ivs ?? makeDefaultIvs(), // 对应右侧“个体值”
                makeDefaultEvs(),       // PvP 公式已不用，占位
                NATURE_MAP[statEditorSlotData.nature],
                60                      // 对战固定 60 级
              )
            : undefined
        }
  onChangeBaseStat={(key, value) => {
    if (statEditorSlot !== null) setSlotBaseStat(statEditorSlot, key, value);
  }}
  onChangeEv={(key, value) => {
    if (statEditorSlot !== null) setSlotIv(statEditorSlot, key, value);
  }}
  onResetBaseStats={() => {
    if (statEditorSlot !== null) resetSlotBaseStats(statEditorSlot);
  }}
  onResetEvs={() => {
    if (statEditorSlot === null) return;
    (Object.keys(makeDefaultEvs()) as StatKey[]).forEach((k) =>
      setSlotIv(statEditorSlot, k, 0),
    );
  }}
/>

      <NatureLevelEditor
        open={natureEditorSlot !== null}
        onClose={closeNatureEditor}
        spiritName={natureEditorSpirit?.name}
        natureId={natureEditorSlotData?.nature ?? 'hardy'}
        
        onChangeNature={(id) => {
          if (natureEditorSlot !== null) setSlotNature(natureEditorSlot, id);
        }}
        
      />
    </div>
  );
}

export default App;